class EmbroideryCustomizer extends HTMLElement {
  constructor() {
    super();

    this.enabled = false;
    this.text = '';
    // Must match the font marked `--active` by default in the Liquid markup
    // (the first one in the list) — this was 'Anton', which isn't even one
    // of the 5 real font choices, so an order placed without touching the
    // font picker recorded a font that was never actually offered.
    this.font = 'UnifrakturMaguntia';
    this.fontLabel = 'Old English';
    this.color = '#E4293A';
    this.colorName = 'Red';
    this.placement = 'Left side';
    this.logo = 'None';
    this.logoColor = '#E4293A';
    this.logoColorName = 'Red';

    this.form = document.querySelector('product-form.product-form form');
    this.variantIdInput = this.form ? this.form.querySelector('.product-variant-id') : null;
    this.quantityInput = document.querySelector('.product-form__quantity .quantity__input');
    this.submitButton = this.form ? this.form.querySelector('.product-form__submit') : null;
    this.dynamicCheckout = this.form ? this.form.querySelector('.buy-buttons__dynamic-checkout') : null;
    this.fastCheckoutButton = this.form ? this.form.querySelector('.buy-buttons__fast-checkout') : null;
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.toggleInput = this.querySelector('.embroidery__switch-input');
    this.panel = this.querySelector('.embroidery__panel');
    this.textPreview = this.querySelector('.embroidery__text-preview-text');
    this.textPreviewLogo = this.querySelector('.embroidery__text-preview-logo');
    this.textInput = this.querySelector('.embroidery__text-input');
    this.charCount = this.querySelector('.embroidery__char-count-current');
    this.logoButtons = Array.from(this.querySelectorAll('.embroidery__logo-option'));
    this.logoColorWrap = this.querySelector('.embroidery__logo-color-wrap');
    this.logoColorButtons = Array.from(this.querySelectorAll('.embroidery__logo-color-option'));
    this.placementButtons = Array.from(this.querySelectorAll('.embroidery__placement-option'));
    this.hatZones = Array.from(this.querySelectorAll('.embroidery__hat-zone'));
    this.fontButtons = Array.from(this.querySelectorAll('.embroidery__font-option'));
    this.fontSamples = Array.from(this.querySelectorAll('.embroidery__font-sample'));
    this.colorButtons = Array.from(this.querySelectorAll('.embroidery__color-option'));
    this.priceTotal = this.querySelector('.embroidery__price-total');
    this.priceLogoRow = this.querySelector('.embroidery__price-row-logo');
    this.priceLogoName = this.querySelector('.embroidery__price-logo-name');

    // Quick-add's own JS strips every <modal-dialog> (ours included) out of
    // the product HTML it fetches before showing it in the quick-add popup,
    // to avoid nesting a modal inside a modal. The "Get inspo" button (a
    // modal-opener, not a modal-dialog) survives that and still renders, but
    // its target gallery no longer exists, so clicking it would silently do
    // nothing. Hide it whenever that's the case rather than leaving a dead
    // button.
    const inspoOpener = this.querySelector('.embroidery__inspo-opener');
    if (inspoOpener && !document.querySelector(inspoOpener.getAttribute('data-modal'))) {
      // Not just the `hidden` attribute — .embroidery__inspo-opener has its
      // own `display: block` rule that outranks the UA stylesheet's
      // `[hidden] { display: none }`, so the attribute alone doesn't
      // actually hide it. Set the style directly instead.
      inspoOpener.style.display = 'none';
    }

    this.basePriceCents = parseInt(this.dataset.basePrice, 10) || 0;
    this.addonPriceCents = parseInt(this.dataset.price, 10) || 0;
    this.addonHandle = this.dataset.productHandle;
    this.logoPriceCents = parseInt(this.dataset.logoPrice, 10) || 0;
    this.logoHandle = this.dataset.logoProductHandle;

    // Look up the add-on variant IDs up front so submitting doesn't have to
    // wait on a product lookup at the last second.
    this.addonVariantIdPromise = this.addonHandle
      ? fetch(`/products/${this.addonHandle}.js`)
          .then((r) => r.json())
          .then((p) => p.variants[0].id)
          .catch(() => null)
      : Promise.resolve(null);
    this.logoVariantIdPromise = this.logoHandle
      ? fetch(`/products/${this.logoHandle}.js`)
          .then((r) => r.json())
          .then((p) => p.variants[0].id)
          .catch(() => null)
      : Promise.resolve(null);

    this.toggleInput.addEventListener('change', this.onToggle.bind(this));
    this.textInput.addEventListener('input', this.onTextInput.bind(this));
    this.logoButtons.forEach((button) => button.addEventListener('click', () => this.onLogoSelect(button)));
    this.logoColorButtons.forEach((button) => button.addEventListener('click', () => this.onLogoColorSelect(button)));
    this.placementButtons.forEach((button) => button.addEventListener('click', () => this.onPlacementSelect(button)));
    this.fontButtons.forEach((button) => button.addEventListener('click', () => this.onFontSelect(button)));
    this.colorButtons.forEach((button) => button.addEventListener('click', () => this.onColorSelect(button)));

    // The form and fast-checkout button live outside this element's own subtree
    // (they're rendered by the sibling buy-buttons snippet), so they're found via
    // a document-wide lookup. If this constructor ever runs more than once for the
    // same page — theme-editor block re-renders, hot-reload swaps — a second
    // instance would find the same shared elements and bind duplicate listeners,
    // causing every click to fire multiple stale /cart/add requests. Guard with a
    // marker so only the first instance ever binds.
    if (this.form && !this.form.dataset.embroideryFormBound) {
      this.form.dataset.embroideryFormBound = 'true';
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    if (this.fastCheckoutButton && !this.fastCheckoutButton.dataset.embroideryBound) {
      this.fastCheckoutButton.dataset.embroideryBound = 'true';
      this.fastCheckoutButton.addEventListener('click', () => this.submit({ redirectToCheckout: true }));
    }

    this.updatePriceDisplay();
    this.updateTextPreview();
    this.updateHatDiagram();
  }

  onToggle() {
    this.enabled = this.toggleInput.checked;
    this.panel.hidden = !this.enabled;
    this.updatePriceDisplay();
    this.updateExpressCheckoutVisibility();
  }

  updateExpressCheckoutVisibility() {
    // Shopify's native "Buy it now" button checks out only this one variant —
    // it can't also include the separate embroidery/logo line items. Swap it
    // for our own button that adds everything in a single request and then
    // jumps straight to checkout, so the fast path still works correctly.
    if (this.dynamicCheckout) this.dynamicCheckout.hidden = this.enabled;
    if (this.fastCheckoutButton) this.fastCheckoutButton.hidden = !this.enabled;
  }

  onTextInput() {
    this.text = this.textInput.value.slice(0, 10);
    if (this.textInput.value !== this.text) this.textInput.value = this.text;
    if (this.charCount) this.charCount.textContent = this.text.length;
    this.updateTextPreview();
  }

  onLogoSelect(button) {
    this.logo = button.dataset.logo;
    this.logoButtons.forEach((b) => {
      b.classList.toggle('embroidery__logo-option--active', b === button);
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    if (this.logoColorWrap) this.logoColorWrap.hidden = this.logo === 'None';
    this.updatePriceDisplay();
    this.updateTextPreview();
  }

  onLogoColorSelect(button) {
    this.logoColor = button.dataset.color;
    this.logoColorName = button.dataset.colorName;
    this.logoColorButtons.forEach((b) => {
      b.classList.toggle('embroidery__logo-color-option--active', b === button);
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.updateTextPreview();
  }

  onPlacementSelect(button) {
    this.placement = button.dataset.placement;
    this.placementButtons.forEach((b) => {
      b.classList.toggle('embroidery__placement-option--active', b === button);
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.updateHatDiagram();
  }

  updateHatDiagram() {
    this.hatZones.forEach((zone) => {
      zone.classList.toggle('embroidery__hat-zone--active', zone.dataset.zone === this.placement);
    });
  }

  onFontSelect(button) {
    this.font = button.dataset.font;
    this.fontLabel = button.dataset.fontLabel || button.dataset.font;
    this.fontButtons.forEach((b) => {
      b.classList.toggle('embroidery__font-option--active', b === button);
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.updateTextPreview();
  }

  onColorSelect(button) {
    this.color = button.dataset.color;
    this.colorName = button.dataset.colorName;
    this.colorButtons.forEach((b) => {
      b.classList.toggle('embroidery__color-option--active', b === button);
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    });
    this.updateTextPreview();
  }

  updateTextPreview() {
    if (this.textPreview) {
      this.textPreview.textContent = this.text || 'Aa';
      this.textPreview.style.fontFamily = `'${this.font}'`;
      this.textPreview.style.color = this.color;
    }
    if (this.fontSamples.length) {
      const sampleText = this.text || 'Nathan';
      this.fontSamples.forEach((sample) => {
        sample.textContent = sampleText;
      });
    }
    if (this.textPreviewLogo) {
      // Only "Heart" has a matching icon drawn right now — future logos added
      // via the theme setting just show as text-only until an icon is added.
      // (SVG elements don't reliably reflect the `hidden` IDL property to the
      // attribute in this browser, so add/remove the attribute explicitly.)
      if (this.logo === 'Heart') {
        this.textPreviewLogo.removeAttribute('hidden');
      } else {
        this.textPreviewLogo.setAttribute('hidden', '');
      }
      this.textPreviewLogo.style.fill = this.logoColor;
    }
  }

  updatePriceDisplay() {
    if (!this.priceTotal) return;
    const hasLogo = this.enabled && this.logo !== 'None';
    let totalCents = this.basePriceCents + (this.enabled ? this.addonPriceCents : 0);
    if (hasLogo) totalCents += this.logoPriceCents;

    if (this.priceLogoRow) this.priceLogoRow.hidden = !hasLogo;
    if (this.priceLogoName && hasLogo) this.priceLogoName.textContent = this.logo;

    this.priceTotal.textContent = this.formatMoney(totalCents);
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'PHP' });
  }

  onFormSubmit(event) {
    // Without embroidery there's only ever one product involved — leave Dawn's
    // native add-to-cart handling completely alone for that common case.
    if (!this.enabled) return;
    event.preventDefault();
    this.submit({ redirectToCheckout: false, triggerButton: this.submitButton });
  }

  buildAddonItems(quantity) {
    return Promise.all([this.addonVariantIdPromise, this.logoVariantIdPromise]).then(
      ([addonVariantId, logoVariantId]) => {
        const items = [];

        if (addonVariantId) {
          items.push({
            id: addonVariantId,
            quantity,
            properties: {
              'Embroidery For': document.title,
              'Embroidery Text': this.text,
              'Embroidery Font': this.fontLabel,
              'Embroidery Thread Color': this.colorName,
              'Embroidery Placement': this.placement,
            },
          });
        } else {
          console.warn('Embroidery add-on product handle is not configured (or not found) in Theme settings.');
        }

        if (this.logo !== 'None') {
          if (logoVariantId) {
            items.push({
              id: logoVariantId,
              quantity,
              properties: {
                'Embroidery For': document.title,
                Logo: this.logo,
                'Logo Thread Color': this.logoColorName,
              },
            });
          } else {
            console.warn('Logo add-on product handle is not configured (or not found) in Theme settings.');
          }
        }

        return items;
      }
    );
  }

  submit({ redirectToCheckout, triggerButton }) {
    if (!this.variantIdInput || !this.variantIdInput.value) return;

    const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
    const button = triggerButton || this.fastCheckoutButton;
    this.setLoading(button, true);

    this.buildAddonItems(quantity)
      .then((addonItems) => {
        const items = [{ id: this.variantIdInput.value, quantity }, ...addonItems];
        const body = { items };
        if (!redirectToCheckout && this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        }

        return fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).then((response) => response.json());
      })
      .then((response) => {
        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }
        if (this.cart) this.cart.renderContents(response);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (!redirectToCheckout) this.setLoading(button, false);
      });
  }

  setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.toggleAttribute('aria-disabled', isLoading);
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }
}

customElements.define('embroidery-customizer', EmbroideryCustomizer);
